/**
 * Carpool Dashboard
 *
 * Stat tiles (drivers, riders, pending verifications, open listings, upcoming bookings,
 * open disputes) → 30-day activity chart (bookings / cancellations / listings) + "Needs
 * attention" → top routes table. Same layout as the Forms dashboard.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconSteeringWheel,
  IconUsers,
  IconHourglassHigh,
  IconDoorEnter,
  IconTicket,
  IconFlag,
  IconRefresh,
  IconShieldCheck,
  IconAlertTriangle,
  IconChevronRight,
  IconMoodSmile,
  IconActivity,
  IconRoute,
  IconCalendarEvent,
  IconInbox,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Stack, Group, Button, Text, Title, SimpleGrid, Skeleton, Grid, ThemeIcon, Alert, Table, Badge } from '@/components/ui';
import { EmptyState } from '@/components/ui/data-display';
import ActionIcon from '@/components/ui/ActionIcon';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import AreaChartWidget from '@/components/advanced/Widgets/charts/AreaChartWidget';
import { useRidesAdminService } from '@actschurch/ccms-sdk';
import { useRidesPermissions } from '@/hooks/rides/useRidesPermissions';
import { StatTile, StatTilesSkeleton, SectionCard, ridesKeys, errorMessage, formatYmdShort, formatTime, RIDES_BASE_PATH } from '../components';

interface AttentionItem {
  key: string;
  icon: React.ReactNode;
  color: string;
  text: string;
  count: number;
  onClick?: () => void;
}

export default function RidesDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const admin = useRidesAdminService();
  const perms = useRidesPermissions();

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ridesKeys.dashboard(),
    queryFn: () => admin.getDashboard(),
  });

  const chartData = useMemo(
    () =>
      (data?.series ?? []).map((p) => ({
        day: formatYmdShort(p.day),
        Bookings: p.bookings,
        Cancellations: p.cancellations,
        Listings: p.listings,
      })),
    [data?.series]
  );
  const totals = useMemo(() => {
    const s = data?.series ?? [];
    return {
      bookings: s.reduce((n, p) => n + p.bookings, 0),
      cancellations: s.reduce((n, p) => n + p.cancellations, 0),
      listings: s.reduce((n, p) => n + p.listings, 0),
    };
  }, [data?.series]);

  const attention: AttentionItem[] = useMemo(() => {
    if (!data) return [];
    const unverified = Math.max(0, data.activeDrivers - data.verifiedDrivers);
    const items: AttentionItem[] = [
      {
        key: 'verifications',
        icon: <IconHourglassHigh size={18} />,
        color: 'yellow',
        text: 'verifications waiting for a physical check',
        count: data.pendingVerifications,
        onClick: perms.canViewVerification ? () => navigate(`${RIDES_BASE_PATH}/verifications`) : undefined,
      },
      {
        key: 'disputes',
        icon: <IconFlag size={18} />,
        color: 'red',
        text: `open dispute${data.openDisputes === 1 ? '' : 's'}`,
        count: data.openDisputes,
        onClick: perms.canManageDisputes ? () => navigate(`${RIDES_BASE_PATH}/disputes`) : undefined,
      },
      {
        key: 'unverified',
        icon: <IconShieldCheck size={18} />,
        color: 'orange',
        text: `driver${unverified === 1 ? '' : 's'} registered but not fully verified`,
        count: unverified,
        onClick: () => navigate(`${RIDES_BASE_PATH}/drivers?status=pending`),
      },
      {
        key: 'cancellations',
        icon: <IconAlertTriangle size={18} />,
        color: 'gray',
        text: 'cancellations in the last 30 days',
        count: data.cancellationsLast30Days,
      },
    ];
    return items.filter((i) => i.count > 0);
  }, [data, perms.canViewVerification, perms.canManageDisputes, navigate]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ridesKeys.all });

  if (isLoading) {
    return (
      <LayoutContainer>
        <FadeIn>
          <Stack gap="lg">
            <Group justify="space-between">
              <Skeleton height={40} width={260} />
              <Skeleton height={36} width={220} />
            </Group>
            <StatTilesSkeleton count={3} />
            <StatTilesSkeleton count={3} />
            <Grid>
              <Grid.Col span={{ base: 12, lg: 8 }}><Skeleton height={340} radius="md" /></Grid.Col>
              <Grid.Col span={{ base: 12, lg: 4 }}><Skeleton height={340} radius="md" /></Grid.Col>
            </Grid>
          </Stack>
        </FadeIn>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <Group justify="space-between" wrap="wrap" align="flex-start">
            <div>
              <Title order={2}>Carpool Dashboard</Title>
              <Text size="sm" c="dimmed">Verified church carpooling at a glance</Text>
            </div>
            <Group gap="sm" data-help-id="rides.dashboard.actions">
              <ActionIcon variant="subtle" size="lg" onClick={refresh} loading={isFetching} aria-label="Refresh">
                <IconRefresh size={18} />
              </ActionIcon>
              {perms.canViewSecurity && (
                <Button variant="light" leftSection={<IconShieldCheck size={16} />} onClick={() => navigate(`${RIDES_BASE_PATH}/security`)}>
                  Security desk
                </Button>
              )}
              <Button leftSection={<IconSteeringWheel size={16} />} onClick={() => navigate(`${RIDES_BASE_PATH}/drivers`)}>
                Drivers
              </Button>
              <PageHelpButton pageKey="rides.dashboard" />
            </Group>
          </Group>

          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              {errorMessage(error, 'Failed to load the dashboard')}
            </Alert>
          )}

          {data && (
            <>
              <SimpleGrid cols={{ base: 2, md: 3, lg: 6 }} data-help-id="rides.dashboard.stats">
                <StatTile
                  helpId="rides.dashboard.stat-drivers"
                  icon={<IconSteeringWheel size={22} />}
                  color="blue"
                  label="Drivers"
                  value={data.activeDrivers}
                  hint={`${data.verifiedDrivers} fully verified`}
                  onClick={() => navigate(`${RIDES_BASE_PATH}/drivers`)}
                />
                <StatTile icon={<IconUsers size={22} />} color="cyan" label="Riders" value={data.riders} hint="Registered in the portal" />
                <StatTile
                  helpId="rides.dashboard.stat-pending"
                  icon={<IconHourglassHigh size={22} />}
                  color="yellow"
                  label="Pending checks"
                  value={data.pendingVerifications}
                  hint={data.pendingVerifications > 0 ? 'Open the verification queue' : 'Nothing waiting'}
                  emphasis={data.pendingVerifications > 0}
                  onClick={perms.canViewVerification ? () => navigate(`${RIDES_BASE_PATH}/verifications`) : undefined}
                />
                <StatTile
                  icon={<IconDoorEnter size={22} />}
                  color="green"
                  label="Open listings"
                  value={data.openListings}
                  hint={`${data.eventsThisMonth} event${data.eventsThisMonth === 1 ? '' : 's'} this month`}
                  onClick={() => navigate(`${RIDES_BASE_PATH}/listings`)}
                />
                <StatTile
                  icon={<IconTicket size={22} />}
                  color="teal"
                  label="Upcoming bookings"
                  value={data.upcomingBookings}
                  hint={`${data.bookingsLast30Days} in the last 30 days`}
                  onClick={() => navigate(`${RIDES_BASE_PATH}/bookings`)}
                />
                <StatTile
                  helpId="rides.dashboard.stat-disputes"
                  icon={<IconFlag size={22} />}
                  color="red"
                  label="Open disputes"
                  value={data.openDisputes}
                  hint={data.openDisputes > 0 ? 'Needs a decision' : 'None open'}
                  emphasis={data.openDisputes > 0}
                  onClick={perms.canManageDisputes ? () => navigate(`${RIDES_BASE_PATH}/disputes`) : undefined}
                />
              </SimpleGrid>

              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, lg: 8 }} data-help-id="rides.dashboard.activity">
                  {totals.bookings + totals.listings > 0 ? (
                    <AreaChartWidget
                      title="Activity"
                      subtitle={`Last 30 days · ${totals.listings.toLocaleString()} listings · ${totals.bookings.toLocaleString()} bookings · ${totals.cancellations.toLocaleString()} cancellations`}
                      data={chartData}
                      xAxisKey="day"
                      height={290}
                      showGrid
                      showLegend
                      areas={[
                        { dataKey: 'Listings', name: 'Listings', color: 'var(--mantine-color-blue-4)', fillOpacity: 0.15 },
                        { dataKey: 'Bookings', name: 'Bookings', color: 'var(--mantine-color-green-6)', fillOpacity: 0.3 },
                        { dataKey: 'Cancellations', name: 'Cancellations', color: 'var(--mantine-color-red-5)', fillOpacity: 0.15 },
                      ]}
                    />
                  ) : (
                    <SectionCard icon={<IconActivity size={18} />} title="Activity" description="Listings and bookings over the last 30 days" h="100%">
                      <EmptyState size="sm" icon={<IconInbox size={32} />} title="No activity in the last 30 days" description="Listings and bookings appear here once drivers and riders start using Carpool." />
                    </SectionCard>
                  )}
                </Grid.Col>

                <Grid.Col span={{ base: 12, lg: 4 }}>
                  <SectionCard helpId="rides.dashboard.attention" icon={<IconAlertTriangle size={18} />} color="orange" title="Needs attention" description="Things worth a look today" h="100%">
                    {attention.length === 0 ? (
                      <EmptyState size="sm" icon={<IconMoodSmile size={32} />} title="All caught up" description="Nothing needs your attention right now." />
                    ) : (
                      <Stack gap={6}>
                        {attention.map((a) => (
                          <Group
                            key={a.key}
                            wrap="nowrap"
                            gap="sm"
                            p="xs"
                            onClick={a.onClick}
                            style={{ borderRadius: 'var(--mantine-radius-md)', cursor: a.onClick ? 'pointer' : 'default', transition: 'background-color 120ms ease' }}
                            onMouseEnter={(e) => {
                              if (a.onClick) (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--mantine-color-default-hover)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.backgroundColor = '';
                            }}
                          >
                            <ThemeIcon size={34} radius="md" variant="light" color={a.color} style={{ flexShrink: 0 }}>
                              {a.icon}
                            </ThemeIcon>
                            <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                              <Text span fw={700}>{a.count.toLocaleString()}</Text> {a.text}
                            </Text>
                            {a.onClick && <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />}
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>

                <Grid.Col span={{ base: 12 }}>
                  <SectionCard
                    helpId="rides.dashboard.top-routes"
                    icon={<IconRoute size={18} />}
                    color="teal"
                    title="Top routes"
                    description="Where drivers are offering lifts right now"
                    actions={
                      <Button variant="subtle" size="xs" rightSection={<IconChevronRight size={14} />} onClick={() => navigate(`${RIDES_BASE_PATH}/listings`)}>
                        All listings
                      </Button>
                    }
                    padding="md"
                  >
                    {data.topRoutes.length === 0 ? (
                      <EmptyState size="sm" icon={<IconCalendarEvent size={32} />} title="No open routes" description="Routes appear once drivers list a ride for an upcoming event." />
                    ) : (
                      <Table>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Collection</Table.Th>
                            <Table.Th>Drop-off</Table.Th>
                            <Table.Th w={120}>Service</Table.Th>
                            <Table.Th w={110} ta="right">Drivers</Table.Th>
                            <Table.Th w={110} ta="right">Open seats</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {data.topRoutes.map((r, i) => (
                            <Table.Tr key={`${r.collectionLocationID}-${r.dropOffLocationID}-${r.serviceTimeStart ?? ''}-${i}`}>
                              <Table.Td><Text size="sm" fw={500}>{r.collectionName}</Text></Table.Td>
                              <Table.Td><Text size="sm">{r.dropOffName}</Text></Table.Td>
                              <Table.Td><Text size="sm" c="dimmed">{formatTime(r.serviceTimeStart, 'Not set')}</Text></Table.Td>
                              <Table.Td ta="right"><Text size="sm" fw={600}>{r.driverCount}</Text></Table.Td>
                              <Table.Td ta="right">
                                <Badge variant="light" color={r.seatsOpen > 0 ? 'green' : 'gray'} styles={{ root: { textTransform: 'none' } }}>{r.seatsOpen}</Badge>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </SectionCard>
                </Grid.Col>
              </Grid>
            </>
          )}
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
